import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { articles, type ArticleSection } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useLang } from '@/hooks/useLang';

function renderSection(section: ArticleSection, index: number, lang: string) {
  switch (section.type) {
    case 'heading':
      return (
        <h2
          key={index}
          id={section.id}
          className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4 scroll-mt-24"
        >
          {section.content}
        </h2>
      );
    case 'text':
      return (
        <div
          key={index}
          className="text-foreground-700 leading-relaxed mb-4"
          dangerouslySetInnerHTML={{
            __html: (section.content || '').replace(
              /<a /g,
              '<a class="text-primary-600 hover:text-primary-700 font-medium" '
            ),
          }}
        />
      );
    case 'list': {
      const items = section.items as string[];
      return (
        <ul key={index} className="list-disc list-inside space-y-2 text-foreground-700 mb-6 pl-1">
          {items.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      );
    }
    case 'table': {
      const headers = section.headers || [];
      const rows = section.rows || [];
      return (
        <div key={index} className="overflow-x-auto mb-6">
          <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
            <thead className="bg-background-100">
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left font-semibold text-foreground-800">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {rows.map((row, ri) => (
                <tr key={ri} className="bg-background-50">
                  {row.map((cell, ci) => (
                    <td key={ci} className={ci === 0 ? 'px-4 py-3 font-medium text-foreground-800' : 'px-4 py-3 text-foreground-600'}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'link-grid': {
      const links = section.items as { label: string; href: string; icon: string }[];
      return (
        <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {links.map((link, i) => (
            <a
              key={i}
              href={link.href.replace(/^\/en\//, '/' + lang + '/')}
              className="flex items-center gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors"
            >
              <i className={`${link.icon} text-primary-500 text-lg`} />
              <span className="text-sm font-medium text-foreground-800">{link.label}</span>
            </a>
          ))}
        </div>
      );
    }
    case 'tip':
      return (
        <div key={index} className="bg-accent-50 border border-accent-200 rounded-lg p-5 mb-6">
          <p className="text-sm text-foreground-700 leading-relaxed">{section.content}</p>
        </div>
      );
    case 'comparison':
      return null;
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { articleSlug } = useParams<{ articleSlug: string }>();
  const article = articles[articleSlug || ''];
  const lang = useLang();

  useEffect(() => {
    if (article) {
      document.title = article.title;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', article.metaDesc);
      window.scrollTo(0, 0);
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">
              Article Not Found
            </h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <a href={`/${lang}/`} className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 font-medium text-sm rounded-md hover:bg-primary-600 transition-colors">
              Back to Home
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero Image */}
        <div className="relative h-[280px] md:h-[380px] overflow-hidden">
          <img
            src={article.image}
            alt={article.h1}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/30 to-transparent" />
        </div>

        {/* Breadcrumbs */}
        <div className="w-full px-4 md:px-6 lg:px-10 py-4 bg-background-100 border-b border-background-200/70">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700 font-medium">{article.h1}</span>
          </nav>
        </div>

        {/* Article Header */}
        <div className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight mb-6">
              {article.h1}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                Published: {article.published}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                Updated: {article.updated}
              </span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {/* Direct Answer */}
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-10">
              <p className="text-lg text-foreground-800 leading-relaxed m-0">
                {article.directAnswer}
              </p>
            </div>

            {/* Table of Contents */}
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-12 mb-4">
              Table of Contents
            </h2>
            <nav className="bg-background-100 border border-background-200/70 rounded-lg p-5 mb-10">
              <ul className="space-y-2 text-sm">
                {article.toc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className="text-primary-600 hover:text-primary-700 transition-colors">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Sections */}
            {article.sections.map((section, index) => renderSection(section, index, lang))}

            {/* FAQ */}
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4 mb-10">
              {article.faq.map((faq, i) => (
                <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                  <h3 className="font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>

            {/* Related Resources */}
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">
              Related Resources
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {article.related.map((link, i) => (
                <a
                  key={i}
                  href={link.href.replace(/^\/en\//, '/' + lang + '/')}
                  className="flex items-center gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors"
                >
                  <i className={`${link.icon} text-primary-500 text-lg`} />
                  <span className="text-sm font-medium text-foreground-800">{link.label}</span>
                </a>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-8 text-center">
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                Ready to Explore the AllChinaBuy Spreadsheet?
              </h3>
              <p className="text-sm text-foreground-600 mb-5">
                Visit our main website for the complete product catalog and latest resources.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors"
              >
                Explore AllChinaBuy Spreadsheet →
              </a>
            </div>

            {/* Disclaimer */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <p className="text-xs text-foreground-500 leading-relaxed">
                <strong>Disclaimer:</strong> This website provides general educational information. It does not guarantee seller quality, product authenticity, shipping outcomes, refunds or transaction results. Always verify current information before purchasing. The AllChinaBuy Spreadsheet is a research resource only — actual purchases are handled through third-party platforms.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}